import React, { useEffect, useMemo, useState } from 'react';
import { ReptoolModal } from '../utilities/ReptoolModal.tsx'
import { Button } from 'react-bootstrap';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { UserInfo, Subscription } from '../../types/UserInfo.ts';
import { formatIntegerCentAmount } from '../utilities/TextFormatters.ts';
import { Carousel } from 'react-responsive-carousel';
import { SubscriptionType } from './types/SubscriptionTypes.ts';
import { SubscriptionTable } from './SubscriptionModal.tsx';
import { SelectSubscriptionPrice } from './SelectSubscriptionPrice.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useRepToolContext } from '../../RepToolContext.tsx';
const CancelSubscription = ({ onCancel }: {
  onCancel: () => void;
}) => {
  const { appendControls } = useRepToolContext();
  const cancelSubscription = () => {
    // Cancel subscription logic
    BasicFetch({
      url: `${(window as any).serverLocation}stripe/cancel_subscription`,
      method: 'POST',
      onSuccess: () => {
        onCancel()
        alert('Subscription successfully canceled')
        window.location.reload()
      },
      onFail: () => {
        alert('An error occurred while canceling the subscription, please try again later. If the problem persists, contact support.')
      }
    })
  }
  useEffect(() => {
    appendControls(<div className='d-flex w-100'>
      <Button variant='outline-light' size='sm' className='mx-auto' onClick={cancelSubscription}>Cancel Subscription</Button>
    </div>)
  }, [])

  return (
    <div className='d-flex flex-column'>
      <h5>Cancel Subscription</h5>
      <p>Are you sure you want to cancel your subscription?</p>
      <p>This will cancel your subscription and you will no longer have access to the premium features. Your data created in member features will still remain but you will no longer be able to create more.</p>
    </div>
  )

}

const SubscriptionItem = ({ subscription, clickFunctions = [] }: {
  subscription: Subscription,
  clickFunctions?: {
    action: () => void,
    text: string
  }[]

}) => {
  return (
    <div className='d-flex flex-column border border-light p-2 mt-3 w-100 rounded'>
      <h5>{subscription.subscription_label}</h5>
      <p>Price: {subscription.subscription_price ? `$${formatIntegerCentAmount(subscription.subscription_price)}/mo` : 'Free'}</p>
      {subscription.end_date && <p>Expries on: {new Date(subscription.end_date).toDateString()}</p>}
      {subscription.next_payment_date && !subscription.end_date && <p>Next Payment Date: {new Date(subscription.next_payment_date).toDateString()}</p>}
      {clickFunctions.map((clickFunction, index) => {
        return <Button key={index} variant='outline-light' className='mx-auto my-2' onClick={clickFunction.action}>{clickFunction.text}</Button>
      })}
    </div>
  )
}


export const ModifySubscriptionModal = ({ closeModal, userInfo }: {
  closeModal: () => void;
  userInfo: UserInfo;
}) => {
  const [canceling, setCanceling] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionType | null>(null);
  const [selectedPrice, setSelectedPrice] = React.useState<number | null>(null);
  const [fetchingPaymentIntent, setFetchingPaymentIntent] = useState(false);

  const activeIndex = useMemo(() => {
    if (canceling) {
      return 0
    } else if (selectedPrice) {
      return 3
    } else if (selectedPlan) {
      return 2
    } else if (modifying) {
      return 1
    } else {
      return 0
    }
  }, [canceling, modifying, selectedPlan, selectedPrice]);

  const title = useMemo(() => {
    if (canceling) {
      return 'Cancel Subscription'
    } else if (modifying) {
      return 'Modify Subscription'
    } else {
      return 'View Subscription'
    }
  }, [canceling, modifying])
  useEffect(() => {
    if (selectedPlan && !selectedPrice) {
      setSelectedPrice(selectedPlan.defaultPrice);
    } else if (!selectedPlan) {
      setSelectedPrice(null);
    }
  }, [selectedPlan, selectedPrice]);


  const modifyPrice = async () => {
    if (!selectedPlan || !selectedPrice) return;
    setFetchingPaymentIntent(true);
    let searchParams = new URLSearchParams({ api_key: localStorage.getItem("reptoolCookie") || '' });


    const priceId = await fetch(`${(window as any).serverLocation}stripe/create_price?${searchParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        price: selectedPrice,
        subscription_type: selectedPlan.value
      })
    }).then(res => res.json()).then(({ priceId }) => priceId);

    const url = `${(window as any).serverLocation}stripe/modify_subscription?${searchParams}`;
    // Step 2: Create subscription
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        priceId,
        subscription_type: selectedPlan.value
      })
    }).then(res => res.json()).then(() => window.location.reload()).catch(() => {
      alert('An error occurred while modifying the subscription, please try again later. If the problem persists, contact support.')
    }).finally(() => {
      setFetchingPaymentIntent(false);
    });
  }

  return (
    <ReptoolModal
      title={title}
      body={
        <div style={{ height: 'calc(95vh - 100px)', overflowY: 'auto' }}>
          <Carousel selectedItem={activeIndex} showThumbs={false} showStatus={false} showArrows={false} showIndicators={false} swipeable={false} >
            <div>
              {canceling && <CancelSubscription onCancel={() => closeModal()} />}
              {!canceling && !modifying && <div className='text-light text-center'>
                <h3>Subscription Details</h3>
                <div className='d-flex flex-column'>
                  {userInfo.active_paid_subscriptions.map((subscription, index) => {
                    const actions = [{
                      action: () => setModifying(true),
                      text: `${userInfo.subscription?.end_date ? 'Renew' : 'Modify'} Subscription`
                    }]
                    if (subscription.cancellable) {
                      actions.push({
                        action: () => setCanceling(true),
                        text: 'Cancel Subscription'
                      })
                    }
                    return <SubscriptionItem key={index} subscription={subscription} clickFunctions={actions} />
                  })}
                </div>
                {false && <>
                  <h5>Current Plan: {userInfo.subscription?.subscription_label} {userInfo.subscription?.subscription_price && !userInfo.subscription?.end_date ? `($${formatIntegerCentAmount(userInfo.subscription?.subscription_price)}/mo)` : ''}</h5>
                  {userInfo.subscription?.end_date && <h5>Expires on: {userInfo.subscription?.end_date ? new Date(userInfo.subscription?.end_date).toDateString() : ''}</h5>}
                  {userInfo.subscription?.next_payment_date && !userInfo.subscription?.end_date && <h5>Next Payment Date: {userInfo.subscription ? new Date(userInfo.subscription?.next_payment_date).toDateString() : ''}</h5>}

                  <div className='d-flex w-100 flex-column mt-5'>
                    <Button variant='outline-light' className='mx-auto mb-5' onClick={() => setModifying(true)}>{userInfo.subscription?.end_date ? 'Renew' : 'Modify'} Subscription</Button>
                    {userInfo.subscription?.cancellable && <Button variant='outline-danger' className='mx-auto' onClick={() => setCanceling(true)}>Cancel Subscription</Button>}
                  </div>
                </>}

              </div>}
            </div>
            <div>
              <p>
                Modify your subscription by selecting a plan below, if the new plan has a different price, you will be charged or credited the difference immediately.
              </p>
              <SubscriptionTable onSelect={(plan) => {
                setSelectedPlan(plan)
              }} currentPlan={userInfo.subscription?.subscription_type} />

            </div>
            <div>
              {selectedPlan && selectedPrice ? <>
                <div className='d-flex'>
                  <Button variant='outline-light' size='sm' className='mb-3' onClick={() => setSelectedPlan(null)}>
                    <FontAwesomeIcon icon={faArrowLeft} className='mr-2' /> Back
                  </Button>
                </div>
                <SelectSubscriptionPrice
                  minPrice={selectedPlan.minPrice}
                  maxPrice={selectedPlan.maxPrice}
                  setSelectedPrice={setSelectedPrice}
                  selectedPrice={selectedPrice}
                  selectedSubscription={selectedPlan}
                  onContinue={modifyPrice}
                  fetchingPaymentIntent={fetchingPaymentIntent}
                  continueText="Update Subscription"
                />
              </> : <></>}
            </div>
          </Carousel>
        </div>
      }
      closeModal={closeModal} />
  )
}
